// Support Module - Redesigned user-facing view with AI agent
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Headphones, Plus, MessageSquare, Clock, CheckCircle,
  AlertCircle, ChevronRight, Send, X, Tag, Flag, RefreshCw,
  HelpCircle, Mic, MicOff, Bot
} from 'lucide-react';
import AIAgentModule from './AIAgentModule';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  { id: 'account', label: 'Mi Cuenta', emoji: '👤' },
  { id: 'platform', label: 'Plataforma', emoji: '💻' },
  { id: 'business', label: 'Problemas con Negocio', emoji: '🏢' },
  { id: 'payment', label: 'Pagos', emoji: '💳' },
  { id: 'technical', label: 'Problemas Técnicos', emoji: '🔧' },
  { id: 'ai', label: 'Asistente IA', emoji: '🤖' },
  { id: 'security', label: 'Seguridad', emoji: '🔒' },
  { id: 'other', label: 'Otro', emoji: '📋' },
];

const PRIORITIES = [
  { id: 'low', label: 'Baja', color: 'text-gray-400', bg: 'bg-gray-500/20' },
  { id: 'normal', label: 'Normal', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  { id: 'high', label: 'Alta', color: 'text-orange-400', bg: 'bg-orange-500/20' },
  { id: 'urgent', label: 'Urgente', color: 'text-red-400', bg: 'bg-red-500/20' },
];

const STATUS_LABELS = { open: 'Abierto', in_progress: 'En Progreso', resolved: 'Resuelto', closed: 'Cerrado' };
const STATUS_COLORS = {
  open: 'text-yellow-400 bg-yellow-500/15',
  in_progress: 'text-blue-400 bg-blue-500/15',
  resolved: 'text-green-400 bg-green-500/15',
  closed: 'text-gray-400 bg-gray-500/15',
};

const SupportModule = ({ user }) => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', category: 'platform', description: '', priority: 'normal' });
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    loadTickets();
    setupRecognition();
  }, [user.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedTicket?.messages]);

  const setupRecognition = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = 'es-MX';
    r.onresult = (e) => { setNewMessage(p => p + e.results[0][0].transcript); setListening(false); };
    r.onerror = r.onend = () => setListening(false);
    recognitionRef.current = r;
  };

  const handleVoice = () => {
    if (!recognitionRef.current) { toast.error('Voz no disponible'); return; }
    if (listening) { recognitionRef.current.stop(); return; }
    recognitionRef.current.start();
    setListening(true);
  };

  const loadTickets = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tickets/user/${user.id}`);
      setTickets(res.data || []);
    } catch (e) {}
    finally { setLoading(false); }
  };

  const createTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.description.trim()) { toast.error('Completa todos los campos'); return; }
    try {
      const res = await axios.post(`${API}/tickets?user_id=${user.id}`, newTicket);
      toast.success(`Ticket ${res.data.ticket_number} creado`);
      setShowCreate(false);
      setNewTicket({ subject: '', category: 'platform', description: '', priority: 'normal' });
      loadTickets();
    } catch { toast.error('Error al crear ticket'); }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket || sending) return;
    setSending(true);
    try {
      await axios.put(`${API}/tickets/${selectedTicket.id}/respond?message=${encodeURIComponent(newMessage)}&responder_type=user`);
      setNewMessage('');
      const res = await axios.get(`${API}/tickets/user/${user.id}`);
      setTickets(res.data);
      const updated = res.data.find(t => t.id === selectedTicket.id);
      if (updated) setSelectedTicket(updated);
    } catch { toast.error('Error al enviar mensaje'); }
    finally { setSending(false); }
  };

  const openCount = tickets.filter(t => t.status === 'open').length;
  const inProgressCount = tickets.filter(t => t.status === 'in_progress').length;
  const resolvedCount = tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length;

  return (
    <div className="p-4 md:p-6 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Headphones className="text-red-400" size={24} />Soporte
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Equipo de soporte disponible 24/7</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadTickets} className="p-2.5 bg-white/5 hover:bg-white/10 rounded-xl transition-colors">
            <RefreshCw size={18} className="text-gray-400" />
          </button>
          <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2">
            <Plus size={18} />Nuevo Ticket
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="glass-panel p-3 rounded-xl text-center">
          <div className="text-xl font-bold text-yellow-400">{openCount}</div>
          <div className="text-gray-500 text-xs mt-0.5">Abiertos</div>
        </div>
        <div className="glass-panel p-3 rounded-xl text-center">
          <div className="text-xl font-bold text-blue-400">{inProgressCount}</div>
          <div className="text-gray-500 text-xs mt-0.5">En Progreso</div>
        </div>
        <div className="glass-panel p-3 rounded-xl text-center">
          <div className="text-xl font-bold text-green-400">{resolvedCount}</div>
          <div className="text-gray-500 text-xs mt-0.5">Resueltos</div>
        </div>
      </div>

      {/* FAQ Quick Links */}
      <div className="glass-panel p-4 mb-6 rounded-2xl">
        <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
          <HelpCircle size={16} className="text-red-400" />Preguntas Frecuentes
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { q: '¿Cómo cambio mi contraseña?', cat: 'account' },
            { q: '¿Cómo cancelo mi suscripción?', cat: 'payment' },
            { q: '¿La IA no responde correctamente?', cat: 'ai' },
            { q: '¿Cómo reporto un problema?', cat: 'technical' },
          ].map((faq, i) => (
            <button key={i}
              onClick={() => { setNewTicket(p => ({ ...p, subject: faq.q, category: faq.cat })); setShowCreate(true); }}
              className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 transition-colors text-left">
              <p className="text-gray-300 text-xs leading-snug">{faq.q}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="glass-panel p-5 mb-6 animate-fade-in-up">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Nuevo Ticket de Soporte</h3>
            <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-white/10 rounded-lg"><X size={18} className="text-gray-400" /></button>
          </div>
          <div className="space-y-3">
            <input value={newTicket.subject} onChange={e => setNewTicket(p => ({ ...p, subject: e.target.value }))}
              placeholder="Asunto del ticket..." className="input-glass w-full" />
            <div className="grid grid-cols-2 gap-3">
              <select value={newTicket.category} onChange={e => setNewTicket(p => ({ ...p, category: e.target.value }))} className="input-glass text-sm">
                {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.label}</option>)}
              </select>
              <select value={newTicket.priority} onChange={e => setNewTicket(p => ({ ...p, priority: e.target.value }))} className="input-glass text-sm">
                {PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <textarea value={newTicket.description} onChange={e => setNewTicket(p => ({ ...p, description: e.target.value }))}
              placeholder="Describe tu problema o consulta en detalle..." className="input-glass w-full resize-none" rows={4} />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowCreate(false)} className="btn-secondary text-sm">Cancelar</button>
              <button onClick={createTicket} className="btn-primary text-sm">Crear Ticket</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tickets list */}
        <div className="space-y-2">
          <h3 className="text-gray-400 text-sm font-medium mb-3">Mis Tickets ({tickets.length})</h3>
          {loading ? (
            <div className="text-center py-8 text-gray-500 text-sm">Cargando...</div>
          ) : tickets.length === 0 ? (
            <div className="glass-panel p-8 text-center">
              <Headphones size={36} className="text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Sin tickets de soporte</p>
              <button onClick={() => setShowCreate(true)} className="btn-primary mt-3 text-sm">Crear ticket</button>
            </div>
          ) : tickets.map(ticket => {
            const cat = CATEGORIES.find(c => c.id === ticket.category) || CATEGORIES[7];
            const pri = PRIORITIES.find(p => p.id === ticket.priority) || PRIORITIES[1];
            return (
              <button key={ticket.id} onClick={() => setSelectedTicket(ticket)}
                className={`w-full glass-panel p-4 text-left transition-all hover:scale-[1.01] rounded-2xl ${selectedTicket?.id === ticket.id ? 'ring-1 ring-red-500/40' : ''}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-red-400 text-xs font-mono">{ticket.ticket_number}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[ticket.status] || STATUS_COLORS.open}`}>
                    {STATUS_LABELS[ticket.status] || ticket.status}
                  </span>
                </div>
                <p className="text-white font-medium text-sm truncate mb-1">{ticket.subject}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{cat.emoji} {cat.label}</span>
                  <span className={`${pri.color}`}>• {pri.label}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Ticket detail */}
        <div className="lg:col-span-2">
          {selectedTicket ? (
            <div className="glass-panel p-5 rounded-2xl flex flex-col" style={{ minHeight: 400, maxHeight: 600 }}>
              {/* Ticket header */}
              <div className="border-b border-white/10 pb-4 mb-4 flex-shrink-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-red-400 text-xs font-mono">{selectedTicket.ticket_number}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[selectedTicket.status]}`}>
                        {STATUS_LABELS[selectedTicket.status]}
                      </span>
                    </div>
                    <h2 className="text-white font-semibold">{selectedTicket.subject}</h2>
                    <p className="text-gray-500 text-xs mt-1">
                      {CATEGORIES.find(c => c.id === selectedTicket.category)?.label} •
                      {PRIORITIES.find(p => p.id === selectedTicket.priority)?.label} prioridad
                    </p>
                  </div>
                  <button onClick={() => setSelectedTicket(null)} className="p-1.5 hover:bg-white/10 rounded-lg flex-shrink-0">
                    <X size={16} className="text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto space-y-3 mb-4 custom-scrollbar">
                {/* Initial description */}
                {selectedTicket.description && (
                  <div className="flex justify-end">
                    <div className="max-w-[80%] bg-red-500/15 border border-red-500/20 p-3 rounded-xl rounded-br-sm">
                      <p className="text-white text-sm">{selectedTicket.description}</p>
                      <p className="text-gray-500 text-xs mt-1">Tú • {new Date(selectedTicket.created_at).toLocaleString('es-MX')}</p>
                    </div>
                  </div>
                )}

                {(selectedTicket.messages || []).map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] p-3 rounded-xl ${msg.from === 'user' ? 'bg-red-500/15 border border-red-500/20 rounded-br-sm' : 'bg-white/5 border border-white/10 rounded-bl-sm'}`}>
                      {msg.from !== 'user' && (
                        <div className="flex items-center gap-1.5 mb-1">
                          <Bot size={12} className="text-red-400" />
                          <span className="text-red-400 text-xs font-medium">Soporte OkBot</span>
                        </div>
                      )}
                      <p className="text-sm text-white">{msg.content}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {msg.from === 'user' ? 'Tú' : 'Soporte'} • {new Date(msg.timestamp).toLocaleString('es-MX')}
                      </p>
                    </div>
                  </div>
                ))}

                {selectedTicket.resolution && (
                  <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-xl">
                    <p className="text-green-400 text-xs font-medium mb-1 flex items-center gap-1">
                      <CheckCircle size={12} />Resolución final
                    </p>
                    <p className="text-gray-300 text-sm">{selectedTicket.resolution}</p>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply */}
              {selectedTicket.status !== 'closed' && (
                <div className="flex gap-2 pt-4 border-t border-white/10 flex-shrink-0">
                  <input value={newMessage} onChange={e => setNewMessage(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                    placeholder="Escribe un mensaje de seguimiento..." className="input-glass flex-1 text-sm" />
                  <button onClick={handleVoice}
                    className={`px-3 rounded-xl transition-colors ${listening ? 'bg-red-500 text-white animate-pulse' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
                    {listening ? <MicOff size={16} /> : <Mic size={16} />}
                  </button>
                  <button onClick={sendMessage} disabled={sending || !newMessage.trim()}
                    className="btn-primary px-4 disabled:opacity-50 flex items-center gap-1.5 text-sm">
                    {sending ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send size={16} />}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="glass-panel p-16 rounded-2xl flex flex-col items-center justify-center text-center">
              <Headphones size={48} className="text-gray-600 mb-4" />
              <h3 className="text-white font-semibold mb-2">Selecciona un ticket</h3>
              <p className="text-gray-500 text-sm">O crea uno nuevo para recibir ayuda personalizada</p>
              <button onClick={() => setShowCreate(true)} className="btn-primary mt-4 text-sm">Crear nuevo ticket</button>
            </div>
          )}
        </div>
      </div>

      {/* AI Support Agent */}
      <AIAgentModule sectionContext="soporte y ayuda" compact={true} placeholder="¿En qué puedo ayudarte hoy?" />
    </div>
  );
};

export default SupportModule;
